import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

class CartCheckoutGate extends Component {
  #initialised = false
  #giftNoteValid = false;
  #dateValid = false;
  #dateRequired = true;

  async connectedCallback() {
    super.connectedCallback?.();

    await this.initialiseAfterMorph();
  }

  disconnectedCallback() {
    this.removeEventListener('cart-gift-note:change', this.onGiftNoteChange);
    this.removeEventListener('cart-delivery-date:change', this.onDateChange);
    this.checkoutButton?.removeEventListener('click', this.onCheckoutClick, true);

    super.disconnectedCallback?.();
  }

  async initialiseAfterMorph() {
    if (!this.isConnected) return;

    await customElements.whenDefined('cart-delivery-date');
    
    if (!this.#initialised) {
      this.#initialised = true;
      this.addEventListener('cart-gift-note:change', this.onGiftNoteChange);
      this.addEventListener('cart-delivery-date:change', this.onDateChange);
    }

    this.bindDom();
    this.refreshCartRequirement();
    this.querySelector('cart-gift-note')?.updateCounter?.();
    this.evaluate();
  }

  bindDom() {
    this.checkoutButton?.removeEventListener('click', this.onCheckoutClick, true);
    this.checkoutButton = this.querySelector('[name="checkout"], #checkout');
    this.additionalButtons = this.querySelector('.additional-checkout-buttons');
    this.checkoutButton?.addEventListener('click', this.onCheckoutClick, true);
  }

  onGiftNoteChange = (event) => {
    this.#giftNoteValid = event.detail.valid;
    this.evaluate();
  };

  onDateChange = (event) => {
    this.#dateValid = event.detail.valid;
    this.#dateRequired = event.detail.required;
    this.evaluate();
  };

  applyCartRequirement() {
    setTimeout(() => {
      const totalItems = Number(this.dataset.totalItems || 0);
      const nationwideCount = Number(this.dataset.nationwideCount || 0);

      const allNationwide = totalItems > 0 && nationwideCount === totalItems;
      const mixed = nationwideCount > 0 && nationwideCount < totalItems;

      const deliveryDate = this.querySelector('cart-delivery-date');

      if (!deliveryDate || typeof deliveryDate.setRequired !== 'function') {
        console.warn('cart-delivery-date is not ready/upgraded yet', deliveryDate);
        return;
      }

      if (allNationwide) {
        this.#dateRequired = false;
        deliveryDate.setRequired(false);
        deliveryDate.showNationwideMessage();
      } else if (mixed) {
        this.#dateRequired = true;
        deliveryDate.setRequired(true);
        deliveryDate.showMixedMessage();
      } else {
        this.#dateRequired = true;
        deliveryDate.setRequired(true);
        deliveryDate.showLocalOnly();
      }
    }, 400)
  }

  evaluate() {
    const canCheckout =
      this.#giftNoteValid &&
      (!this.#dateRequired || this.#dateValid);

    if (this.additionalButtons) {
      this.additionalButtons.hidden = !canCheckout;
    }

    if (this.checkoutButton) {
      this.checkoutButton.disabled = !canCheckout;
      this.checkoutButton.setAttribute('aria-disabled', String(!canCheckout));
    }
  }

  onCheckoutClick = (event) => {
    const canCheckout =
      this.#giftNoteValid &&
      (!this.#dateRequired || this.#dateValid);

    if (canCheckout) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.querySelector('cart-gift-note')?.validateBeforeCheckout?.();
  };

  async refreshCartRequirement() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    const cart = await response.json();

    let nationwideCount = 0;
    const totalItems = cart.items.length;

    await Promise.all(
      cart.items.map(async (item) => {
        const response = await fetch(`${window.Shopify.routes.root}products/${item.handle}.js`);
        const product = await response.json();

        if (product.tags?.includes('distribution-nationwide')) {
          nationwideCount += 1;
        }
      })
    );

    this.dataset.totalItems = String(totalItems);
    this.dataset.nationwideCount = String(nationwideCount);

    this.applyCartRequirement();
    this.evaluate();
  }

  onCartUpdated = debounce(() => {
    this.refreshCartRequirement();
  }, 300);
}

if (!customElements.get('cart-checkout-gate')) {
  customElements.define('cart-checkout-gate', CartCheckoutGate);
}

const msg = document.querySelector('.cart-delivery-date__message');

const hiddenDescriptor = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'hidden');

if (msg) {
  Object.defineProperty(msg, 'hidden', {
    set(value) {
      console.trace('message.hidden set', value);
      hiddenDescriptor.set.call(this, value);
    },
    get() {
      return hiddenDescriptor.get.call(this);
    },
  });


  const originalInnerHTML = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');

  Object.defineProperty(msg, 'innerHTML', {
    set(value) {
      console.trace('message.innerHTML set', value);
      originalInnerHTML.set.call(this, value);
    },
    get() {
      return originalInnerHTML.get.call(this);
    },
  });
}

const gate = document.querySelector('cart-checkout-gate');

// watch for load of component 

function initialiseCartCheckoutGates(root = document) {
  root.querySelectorAll?.('cart-checkout-gate').forEach((gate) => {
    gate.initialiseAfterMorph?.();
  });
}

const cartGateObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;

      if (node.matches('cart-checkout-gate')) {
        node.initialiseAfterMorph?.();
        continue;
      }

      initialiseCartCheckoutGates(node);
    }
  }
});

cartGateObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});

initialiseCartCheckoutGates();